import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { Check, ChevronDown, Clock3, Copy, Eye, FileText, Filter, Info, Mail, Pencil, Undo2, UserRound } from "lucide-react";
import { callApi } from "../api/api";
import { useAccess } from "../context/AccessContext";
import { useWorkspace } from "../context/WorkspaceContext";
import MetadataReviewPanel from "../components/MetadataReviewPanel";
import UnderwritingWorkAction from "../components/UnderwritingWorkAction";
import WorkActionTemplate from "../components/WorkActionTemplate";
import Pagination from "../components/Pagination";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

const PAGE_SIZE = 10;
const SHOW_VERIFIED_RECORDS = false;
const SELECT_LIMIT = 100;
const LINE_TONES = {
  DWF: { background: "#dbeafe", color: "#1d4ed8" },
  COM: { background: "#ede9fe", color: "#6d28d9" },
  CEA: { background: "#dcfce7", color: "#15803d" },
};

function businessLineQuery(lines, options) {
  if (!options.length || !lines.length || lines.length === options.length) return "";
  return options
    .map((item) => item.code)
    .filter((code) => lines.includes(code))
    .join(",");
}

function sameId(left, right) {
  return String(left) === String(right);
}

function displayName(user) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return name || user.email_address;
}

function initials(value) {
  const source = String(value || "").split("@")[0];
  const parts = source.split(/[.\s_-]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function formatEntry(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function outcomeStatus(row) {
  if (row.outcome !== "Routed") return "Completed";
  const team = String(row.routed_to_team || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return team ? `Routed to ${team}` : "Routed";
}

function formatQueueStatus(status) {
  if (!status) return "—";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function fileName(path) {
  if (!path) return "";
  const parts = String(path).split("/");
  return parts[parts.length - 1];
}

function writePreviewTab(tab, html) {
  if (!tab || tab.closed) return;
  tab.document.open();
  tab.document.write(html);
  tab.document.close();
}

function openPreviewTab() {
  const tab = window.open("", "_blank");
  if (!tab) return null;
  writePreviewTab(
    tab,
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preview</title></head><body style="margin:0;padding:24px;font-family:Segoe UI,sans-serif;color:#334155;font-size:14px">Loading preview...</body></html>`,
  );
  return tab;
}

function groupVerified(rows) {
  const map = {};
  rows.forEach((row) => {
    if (!map[row.msg_id]) {
      const attachments = [];
      if (row.email_storage_path) {
        attachments.push({
          name: fileName(row.email_storage_path),
          path: row.email_storage_path,
          type: "email",
        });
      }
      map[row.msg_id] = { ...row, attachments };
    }
    if (row.attachment_name) {
      map[row.msg_id].attachments.push({
        name: row.attachment_name,
        path: row.attachment_storage_path,
        type: "attachment",
      });
    }
  });
  return Object.values(map);
}

function splitCounts(total, users) {
  const base = Math.floor(total / users.length);
  const remainder = total % users.length;
  return users.map((user, index) => ({
    user,
    count: base + (index < remainder ? 1 : 0),
  }));
}

function lineLabel(lob, options) {
  const value = String(lob || "").trim();
  if (!value) return "";
  const match = options.find((item) => (
    item.code.toLowerCase() === value.toLowerCase()
    || item.name.toLowerCase() === value.toLowerCase()
  ));
  return match?.name || value;
}

function AssignedSubject({ text }) {
  const label = String(text || "").trim();
  const textRef = useRef(null);
  const hideTimer = useRef(null);
  const copiedTimer = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [box, setBox] = useState(null);

  useEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;
    const measure = () => setTruncated(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [label]);

  useEffect(() => () => {
    clearTimeout(hideTimer.current);
    clearTimeout(copiedTimer.current);
  }, []);

  const place = useCallback(() => {
    const node = textRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = 320;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const placeAbove = window.innerHeight - rect.bottom < 140 && rect.top > 140;
    setBox({
      left,
      top: placeAbove ? rect.top - 8 : rect.bottom + 8,
      transform: placeAbove ? "translateY(-100%)" : "none",
    });
  }, []);

  const show = () => {
    if (!truncated || !label) return;
    clearTimeout(hideTimer.current);
    place();
    setOpen(true);
  };

  const scheduleHide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setOpen(false);
      setCopied(false);
    }, 140);
  };

  useEffect(() => {
    if (!open) return undefined;
    const update = () => place();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, place]);

  const copySubject = async () => {
    try {
      await navigator.clipboard.writeText(label);
    } catch {
      return;
    }
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <span
        ref={textRef}
        style={{ ...styles.subjectText, ...styles.subjectTextClamp }}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
      >
        {label || "—"}
      </span>
      {open && box && createPortal(
        <div
          role="tooltip"
          style={{ ...styles.subjectTooltip, ...box }}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
        >
          <div style={styles.subjectTooltipText}>{label}</div>
          <button type="button" style={styles.subjectCopy} onClick={copySubject}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

function ReturnReason({ reason }) {
  const [open, setOpen] = useState(false);
  const text = String(reason || "").trim();
  if (!text) return null;
  return (
    <div style={styles.returnReason}>
      <Undo2 size={12} strokeWidth={2.25} style={styles.returnReasonIcon} />
      <span style={styles.returnReasonText}>{text}</span>
      <button
        type="button"
        style={styles.returnInfo}
        aria-label="Return reason"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Info size={14} strokeWidth={2} />
        {open && (
          <span role="tooltip" style={styles.returnTooltip}>
            {text}
          </span>
        )}
      </button>
    </div>
  );
}

function BusinessLineBadge({ lob, options }) {
  const label = lineLabel(lob, options);
  if (!label) return "—";
  return (
    <span style={{ ...styles.lineBadge, ...(LINE_TONES[label.toUpperCase()] || styles.lineBadgeFallback) }}>
      {label}
    </span>
  );
}

function BusinessLineFilter({ options, applied, onApply }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(applied);
  const panelRef = useRef(null);
  const label = !options.length || !applied.length || applied.length === options.length
    ? "All business lines"
    : options.filter((item) => applied.includes(item.code)).map((item) => item.name).join(", ");

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!panelRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggleDraft = (code) => {
    setDraft((current) => (
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code]
    ));
  };

  return (
    <div style={styles.lineFilter} ref={panelRef}>
      <button
        type="button"
        style={styles.lineTrigger}
        aria-expanded={open}
        aria-label="LOB"
        onClick={() => {
          setOpen((current) => {
            if (!current) {
              setDraft(applied.length ? applied : options.map((item) => item.code));
            }
            return !current;
          });
        }}
      >
        <Filter size={14} />
        <span style={styles.lineTriggerLabel}>LOB</span>
        <span style={styles.lineTriggerDivider} />
        <span style={styles.lineTriggerValue} title={label}>{label}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div style={styles.linePanel}>
          <div style={styles.linePanelHead}>
            <div>
              <div style={styles.linePanelTitle}>LOB</div>
              <div style={styles.linePanelMeta}>
                {options.length} option{options.length === 1 ? "" : "s"} available
              </div>
            </div>
            <div style={styles.linePanelLinks}>
              <button type="button" style={styles.lineLink} onClick={() => setDraft(options.map((item) => item.code))}>
                Select all
              </button>
              <button type="button" style={styles.lineLink} onClick={() => setDraft([])}>
                Clear
              </button>
            </div>
          </div>
          <div style={styles.lineOptions}>
            {options.map((item) => (
              <label key={item.code} style={styles.lineOption}>
                <input
                  type="checkbox"
                  checked={draft.includes(item.code)}
                  onChange={() => toggleDraft(item.code)}
                />
                {item.name}
              </label>
            ))}
          </div>
          <div style={styles.linePanelFoot}>
            <span style={styles.linePanelMeta}>
              {draft.length} of {options.length} selected
            </span>
            <div style={styles.linePanelActions}>
              <button type="button" style={styles.secondaryButton} onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => {
                  const selected = options
                    .map((item) => item.code)
                    .filter((code) => draft.includes(code));
                  onApply(selected.length ? selected : options.map((item) => item.code));
                  setOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamQueue({ mode = "team" }) {
  const isMine = mode === "mine";
  const { instance, accounts } = useMsal();
  const { isAdmin } = useAccess();
  const { teams, teamCode } = useWorkspace();
  const [tab, setTab] = useState("pending");
  const [filter, setFilter] = useState("unassigned");
  const [lineOptions, setLineOptions] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const lineOptionsRef = useRef([]);
  lineOptionsRef.current = lineOptions;
  const [senderEmail, setSenderEmail] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedSenderEmail, setAppliedSenderEmail] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [completedBy, setCompletedBy] = useState("");
  const [appliedPolicyNumber, setAppliedPolicyNumber] = useState("");
  const [appliedCompletedBy, setAppliedCompletedBy] = useState("");
  const requestId = useRef(0);
  const unassignedTotalRef = useRef(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [unassignedTotal, setUnassignedTotal] = useState(null);
  unassignedTotalRef.current = unassignedTotal;
  const [assignedTotal, setAssignedTotal] = useState(null);
  const [returnedTotal, setReturnedTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [assigneeHolders, setAssigneeHolders] = useState([]);
  const [selectCount, setSelectCount] = useState("");
  const [selectError, setSelectError] = useState("");
  const [filterScreen, setFilterScreen] = useState(mode);
  if (filterScreen !== mode) {
    setFilterScreen(mode);
    setSenderEmail("");
    setFromDate("");
    setToDate("");
    setAppliedSenderEmail("");
    setAppliedFromDate("");
    setAppliedToDate("");
    setPolicyNumber("");
    setCompletedBy("");
    setAppliedPolicyNumber("");
    setAppliedCompletedBy("");
    setPage(1);
    setSelectedIds([]);
    setSelectError("");
  }
  const [selecting, setSelecting] = useState(false);
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const selectMenuRef = useRef(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewOrigin, setReviewOrigin] = useState(null);
  const [uwRow, setUwRow] = useState(null);
  const [templateRow, setTemplateRow] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const filesystem = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";

  const workspace = teams?.find((team) => team.code === teamCode) || teams?.[0] || null;
  const queueCode = workspace?.queue_code || "";
  const queueLabel = workspace?.queue_label || "";
  const queueSource = workspace?.queue_source || "";
  const canManageQueue = Boolean(workspace?.can_manage);
  const activeTeam = useMemo(() => {
    if (!queueCode) return null;
    return {
      code: queueCode,
      label: queueLabel,
      source: queueSource,
      can_manage: canManageQueue,
    };
  }, [queueCode, queueLabel, queueSource, canManageQueue]);
  const operationsOutcomes = !isMine && tab === "completed";

  const roleRefreshAfter = useRef(0);
  const roleRefreshPending = useRef(false);
  const previousTeam = useRef("");

  useEffect(() => {
    if (!teamCode) return;
    if (!previousTeam.current) {
      previousTeam.current = teamCode;
      return;
    }
    if (previousTeam.current === teamCode) return;
    previousTeam.current = teamCode;
    setPage(1);
    setFilter("unassigned");
    setBusinessLines(lineOptionsRef.current.map((item) => item.code));
    setTab("pending");
    setSelectedIds([]);
    setAssigneeId("");
    setExpandedId(null);
    setReviewRow(null);
    setUwRow(null);
    setTemplateRow(null);
    setError("");
  }, [teamCode]);

  useEffect(() => {
    if (!accounts.length) return undefined;
    let cancelled = false;
    callApi(instance, accounts, "GET", "/api/admin/lookup/line_of_business")
      .then((response) => {
        if (cancelled) return;
        const options = (response?.data?.data || [])
          .map((item) => ({
            code: String(item.lookup_code || "").trim(),
            name: String(item.lookup_name || item.lookup_code || "").trim(),
          }))
          .filter((item) => item.code);
        setLineOptions(options);
        setBusinessLines((current) => (
          current.length
            ? current.filter((code) => options.some((item) => item.code === code))
            : options.map((item) => item.code)
        ));
      })
      .catch(() => {
        if (!cancelled) setLineOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, instance]);

  const verifiedRows = useMemo(
    () => (tab === "verified" ? groupVerified(rows) : []),
    [rows, tab],
  );
  const pendingRows = useMemo(() => {
    const seen = new Set();
    return rows.filter((row) => {
      if (seen.has(row.msg_id)) return false;
      seen.add(row.msg_id);
      return true;
    });
  }, [rows]);
  const visibleRows = tab === "verified" ? verifiedRows : pendingRows;
  const selectedOnPage = visibleRows.filter((row) =>
    selectedIds.some((id) => sameId(id, row.msg_id)),
  );
  const allSelected =
    visibleRows.length > 0 && selectedOnPage.length === visibleRows.length;

  const refreshCount = useCallback((params, currentRequest, apply) => {
    const routed = Boolean(params.routed);
    const nextParams = { ...params, countOnly: routed ? undefined : 1, page: 1, pageSize: 1 };
    delete nextParams.routed;
    callApi(
      instance,
      accounts,
      "GET",
      routed ? "/api/team-queue/routed" : "/api/team-queue/operations",
      null,
      {
      params: nextParams,
    })
      .then((response) => {
        if (currentRequest !== requestId.current) return;
        apply(response?.data?.total ?? 0);
      })
      .catch(() => {});
  }, [accounts, instance]);

  const loadQueue = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError("");
    setRows([]);
    try {
      const dateParams = {
        sender_email: appliedSenderEmail || undefined,
        from_date: appliedFromDate || undefined,
        to_date: appliedToDate || undefined,
      };

      if (teams == null) return;
      if (!activeTeam?.code) {
        setRows([]);
        setTotal(0);
        setUnassignedTotal(0);
        setAssignedTotal(0);
        setReturnedTotal(0);
        return;
      }

      if (isMine) {
        if (!activeTeam?.code) return;
        const lineQuery = businessLineQuery(businessLines, lineOptions);
        const lineParams = activeTeam.code === "UNDERWRITING" && lineQuery
          ? { business_line: lineQuery }
          : {};
        const response = await callApi(
          instance,
          accounts,
          "GET",
          "/api/team-queue/work-items",
          null,
          {
            params: {
              team_code: activeTeam.code,
              page,
              pageSize: PAGE_SIZE,
              ...dateParams,
              ...lineParams,
            },
          },
        );
        if (currentRequest !== requestId.current) return;
        const result = response?.data || {};
        setRows((result.data || []).map((row) => ({ ...row, attachmentsLoaded: false })));
        setTotal(result.total ?? null);
        if (result.total == null) {
          refreshCount({ mine: 1, ...dateParams }, currentRequest, setTotal);
        }
        return;
      }

      if (tab === "completed") {
        const routed = activeTeam?.source === "routed";
        const response = await callApi(
          instance,
          accounts,
          "GET",
          routed ? "/api/team-queue/routed" : "/api/team-queue/operations",
          null,
          {
            params: {
              scope: "completed",
              page,
              pageSize: PAGE_SIZE,
              ...(routed ? { team_code: activeTeam.code } : {}),
              sender_email: appliedSenderEmail || undefined,
              from_date: appliedFromDate || undefined,
              to_date: appliedToDate || undefined,
              policy_number: appliedPolicyNumber || undefined,
              completed_by: appliedCompletedBy || undefined,
            },
          },
        );
        if (currentRequest !== requestId.current) return;
        const result = response?.data || {};
        setRows((result.data || []).map((row) => ({ ...row, attachmentsLoaded: false })));
        setTotal(result.total ?? 0);
        return;
      }

      if (tab === "verified") {
        const response = await callApi(
          instance,
          accounts,
          "GET",
          "/api/metadata/verified",
          null,
          {
            params: {
              page,
              pageSize: PAGE_SIZE,
              ...dateParams,
            },
          },
        );
        if (currentRequest !== requestId.current) return;
        setRows(response?.data?.data || []);
        setTotal(response?.data?.total || 0);
        return;
      }

      const scope = filter === "assigned"
        ? "in_progress"
        : filter === "returned"
          ? "returned"
          : "available";
      const routed = activeTeam?.source === "routed";
      const lineQuery = businessLineQuery(businessLines, lineOptions);
      const lineParams = routed && activeTeam?.code === "UNDERWRITING" && lineQuery
        ? { business_line: lineQuery }
        : {};
      const assigneeParams = scope === "in_progress" && assigneeId
        ? { assignee_id: assigneeId }
        : {};
      const response = await callApi(
        instance,
        accounts,
        "GET",
        routed ? "/api/team-queue/routed" : "/api/team-queue/operations",
        null,
        {
          params: {
            scope,
            page,
            pageSize: PAGE_SIZE,
            ...(routed ? { team_code: activeTeam.code } : {}),
            ...dateParams,
            ...lineParams,
            ...assigneeParams,
            ...(scope === "available" && unassignedTotalRef.current != null && !lineQuery
              ? { known_total: unassignedTotalRef.current }
              : {}),
          },
        },
      );
      if (currentRequest !== requestId.current) return;
      const result = response?.data || {};
      setRows((result.data || []).map((row) => ({ ...row, attachmentsLoaded: false })));
      setTotal(result.total ?? null);
      const totals = {
        available: setUnassignedTotal,
        in_progress: setAssignedTotal,
        returned: setReturnedTotal,
      };
      const currentSetter = totals[scope] || setUnassignedTotal;
      if (result.total == null) {
        refreshCount({ scope, ...dateParams, ...assigneeParams }, currentRequest, (value) => {
          setTotal(value);
          if (!assigneeId) currentSetter(value);
        });
      } else if (!assigneeId) {
        currentSetter(result.total);
      }
      if (assigneeId && scope === "in_progress") {
        refreshCount({
          scope,
          ...dateParams,
          ...lineParams,
          ...(routed ? { team_code: activeTeam.code, routed: 1 } : {}),
        }, currentRequest, setAssignedTotal);
      }
      Object.entries(totals).forEach(([countScope, apply]) => {
        if (countScope === scope) return;
        refreshCount({
          scope: countScope,
          ...dateParams,
          ...lineParams,
          ...(routed ? { team_code: activeTeam.code, routed: 1 } : {}),
        }, currentRequest, apply);
      });
    } catch (loadError) {
      if (currentRequest !== requestId.current) return;
      setError(loadError.message || "Failed to load the Operations queue");
      setRows([]);
      setTotal(null);
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
        if (roleRefreshPending.current && currentRequest > roleRefreshAfter.current) {
          roleRefreshPending.current = false;
          window.dispatchEvent(new Event("portal-view-ready"));
        }
      }
    }
  }, [
    accounts,
    appliedCompletedBy,
    appliedFromDate,
    appliedPolicyNumber,
    appliedSenderEmail,
    appliedToDate,
    businessLines,
    lineOptions,
    assigneeId,
    filter,
    instance,
    isMine,
    page,
    activeTeam,
    refreshCount,
    tab,
    teams,
  ]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const changeFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
    setSelectedIds([]);
    setAssigneeId("");
    setExpandedId(null);
  };

  const applyAssignee = (nextId) => {
    setAssigneeId(nextId);
    setPage(1);
    setSelectedIds([]);
    setExpandedId(null);
  };

  useEffect(() => {
    if (isMine || filter !== "assigned" || !activeTeam?.code || !accounts.length) {
      setAssigneeHolders([]);
      return undefined;
    }
    let cancelled = false;
    callApi(instance, accounts, "GET", "/api/team-queue/assignee-holders", null, {
      params: { team_code: activeTeam.code },
    })
      .then((response) => {
        if (cancelled) return;
        const list = response?.data?.data || [];
        setAssigneeHolders(list);
        setAssigneeId((current) => (
          current && !list.some((item) => sameId(item.user_id, current)) ? "" : current
        ));
      })
      .catch(() => {
        if (!cancelled) setAssigneeHolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, instance, isMine, filter, activeTeam, assignedTotal]);

  const applyBusinessLines = (lines) => {
    setBusinessLines(lines);
    setPage(1);
    setSelectedIds([]);
    setExpandedId(null);
  };

  const handlePreview = async (path) => {
    if (previewLoading || !path) return;
    const { filesystem: container, path: filePath } = resolveAdlsPath(
      path,
      filesystem,
    );
    const lower = filePath.toLowerCase();
    const isEmail = lower.endsWith(".eml");
    const previewTab = openPreviewTab();
    if (!previewTab) {
      setError("The browser blocked the preview tab.");
      return;
    }
    try {
      setPreviewLoading(true);
      const needsBlob =
        lower.endsWith(".tif") ||
        lower.endsWith(".tiff") ||
        lower.endsWith(".heic") ||
        lower.endsWith(".heif");
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      if (isEmail || needsBlob) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${process.env.REACT_APP_BACKEND_CLIENT_ID}/access_as_user`],
        });
        const view = isEmail ? "&view=body" : "";
        const response = await fetch(
          `${baseUrl}/api/adls/preview?filesystem=${encodeURIComponent(container)}&path=${encodeURIComponent(filePath)}${view}`,
          { headers: { Authorization: `Bearer ${token.accessToken}` } },
        );
        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }
        if (isEmail) {
          const email = await response.json();
          if (!email?.html) throw new Error("Empty preview response");
          writePreviewTab(previewTab, email.html);
          return;
        }
        const blob = await response.blob();
        if (!blob?.size) throw new Error("Empty preview response");
        const blobUrl = URL.createObjectURL(blob);
        previewTab.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        return;
      }

      const response = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        { params: { filesystem: container, path: filePath } },
      );
      if (!response?.data?.url) throw new Error("Preview URL missing");
      previewTab.location.href = response.data.url;
    } catch (previewError) {
      const message = previewError.message || "Failed to preview document";
      writePreviewTab(
        previewTab,
        `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preview</title></head><body style="margin:0;padding:24px;font-family:Segoe UI,sans-serif;color:#b91c1c;font-size:14px">${message.replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[char]))}</body></html>`,
      );
      setError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!selectMenuOpen) return undefined;
    const close = (event) => {
      if (!selectMenuRef.current?.contains(event.target)) setSelectMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [selectMenuOpen]);

  useEffect(() => {
    if (!selectMenuOpen) setSelectCount("");
  }, [selectMenuOpen]);

  const toggleAll = () => {
    const pageIds = visibleRows.map((row) => row.msg_id);
    if (allSelected) {
      setSelectedIds((current) => {
        const next = current.filter(
          (id) => !pageIds.some((pageId) => sameId(pageId, id)),
        );
        if (!next.length) setSelectError("");
        return next;
      });
      return;
    }
    setSelectedIds((current) => {
      const next = [...current];
      pageIds.forEach((pageId) => {
        if (!next.some((id) => sameId(id, pageId))) next.push(pageId);
      });
      if (next.length > SELECT_LIMIT) {
        setError(`Select up to ${SELECT_LIMIT} items`);
        return current;
      }
      return next;
    });
  };

  const selectBatch = async () => {
    const count = Number(selectCount);
    if (!Number.isInteger(count) || count < 1 || count > SELECT_LIMIT) {
      setSelectError(`Enter a number from 1 to ${SELECT_LIMIT}`);
      return;
    }
    const available = total == null ? null : Number(total);
    if (available != null && Number.isFinite(available) && count > available) {
      setSelectError(available < 1
        ? "No items match this list"
        : `Only ${available} ${available === 1 ? "item is" : "items are"} available`);
      return;
    }
    const scope = filter === "assigned"
      ? "in_progress"
      : filter === "returned"
        ? "returned"
        : "available";
    const routed = activeTeam?.source === "routed";
    try {
      setSelecting(true);
      setError("");
      setSelectError("");
      const response = await callApi(
        instance,
        accounts,
        "GET",
        routed ? "/api/team-queue/routed" : "/api/team-queue/operations",
        null,
        {
          params: {
            ids_only: 1,
            limit: count,
            scope,
            ...(routed ? { team_code: activeTeam.code } : {}),
            ...(routed && activeTeam?.code === "UNDERWRITING" && businessLineQuery(businessLines, lineOptions)
              ? { business_line: businessLineQuery(businessLines, lineOptions) }
              : {}),
            sender_email: appliedSenderEmail || undefined,
            from_date: appliedFromDate || undefined,
            to_date: appliedToDate || undefined,
            ...(scope === "in_progress" && assigneeId ? { assignee_id: assigneeId } : {}),
          },
        },
      );
      const ids = response?.data?.data || [];
      if (ids.length < count) {
        setSelectError(ids.length < 1
          ? "No items match this list"
          : `Only ${ids.length} ${ids.length === 1 ? "item is" : "items are"} available`);
        return;
      }
      setSelectedIds(ids);
      setSelectMenuOpen(false);
    } catch (selectError) {
      setError(selectError.message || "Failed to select items");
    } finally {
      setSelecting(false);
    }
  };

  const mergeAttachments = async (row) => {
    if (row.attachmentsLoaded !== false) return row;
    const response = await callApi(
      instance,
      accounts,
      "GET",
      "/api/team-queue/operations/attachments",
      null,
      { params: { msg_id: row.msg_id } },
    );
    const files = [
      ...(row.attachments || []).filter((file) => file.type === "email"),
      ...(response?.data?.data || []),
    ];
    const next = { ...row, attachments: files, attachmentsLoaded: true };
    setRows((current) => current.map((item) => (
      item.msg_id === row.msg_id ? next : item
    )));
    return next;
  };

  const toggleAttachments = async (row) => {
    if (expandedId === row.msg_id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.msg_id);
    try {
      await mergeAttachments(row);
    } catch (attachmentError) {
      setError(attachmentError.message || "Failed to load attachments");
    }
  };

  const openReview = (row, origin) => {
    const underwriting = activeTeam?.code === "UNDERWRITING";
    const intake = activeTeam?.source === "intake";
    const template = activeTeam?.source === "routed" && !underwriting;
    if (!underwriting && !intake && !template) return;
    if (underwriting) {
      setUwRow(row);
    } else if (template) {
      setTemplateRow(row);
    } else {
      setReviewOrigin(origin || null);
      setReviewRow(row);
    }
    if (row.attachmentsLoaded !== false) return;
    mergeAttachments(row)
      .then((next) => {
        if (underwriting) {
          setUwRow((current) => (current?.msg_id === row.msg_id ? next : current));
          return;
        }
        if (template) {
          setTemplateRow((current) => (current?.msg_id === row.msg_id ? next : current));
          return;
        }
        setReviewRow((current) => (current?.msg_id === row.msg_id ? next : current));
      })
      .catch((attachmentError) => {
        setError(attachmentError.message || "Failed to load attachments");
      });
  };

  const toggleRow = (msgId) => {
    setSelectedIds((current) => {
      if (current.some((id) => sameId(id, msgId))) {
        const next = current.filter((id) => !sameId(id, msgId));
        if (!next.length) setSelectError("");
        return next;
      }
      if (current.length >= SELECT_LIMIT) {
        setError(`Select up to ${SELECT_LIMIT} items`);
        return current;
      }
      return [...current, msgId];
    });
  };

  const revertSelected = async () => {
    if (!activeTeam?.code) return;
    try {
      setReverting(true);
      setError("");
      await callApi(instance, accounts, "POST", "/api/team-queue/revert", {
        msg_ids: selectedIds,
        team_code: activeTeam.code,
      });
      setSelectedIds([]);
      setRevertOpen(false);
      await loadQueue();
    } catch (revertError) {
      setError(revertError.message || "Failed to revert items");
      setRevertOpen(false);
    } finally {
      setReverting(false);
    }
  };

  const search = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      setError("From date must be before the to date");
      return;
    }
    setError("");
    setAppliedSenderEmail(senderEmail.trim());
    setAppliedFromDate(fromDate ? new Date(fromDate).toISOString() : "");
    setAppliedToDate(toDate ? new Date(toDate).toISOString() : "");
    setAppliedPolicyNumber(policyNumber.trim());
    setAppliedCompletedBy(completedBy.trim());
    setPage(1);
    setSelectedIds([]);
  };

  const clearFilters = () => {
    setSenderEmail("");
    setFromDate("");
    setToDate("");
    setAppliedSenderEmail("");
    setAppliedFromDate("");
    setAppliedToDate("");
    setPolicyNumber("");
    setCompletedBy("");
    setAppliedPolicyNumber("");
    setAppliedCompletedBy("");
    setPage(1);
    setSelectedIds([]);
  };

  const headerCount = isMine || tab === "pending"
    ? (isMine
      ? total
      : (unassignedTotal == null || assignedTotal == null
        ? null
        : unassignedTotal + assignedTotal))
    : total;
  const pagedTotal = total == null
    ? (visibleRows.length === PAGE_SIZE
      ? page * PAGE_SIZE + 1
      : (page - 1) * PAGE_SIZE + visibleRows.length)
    : total;
  const canManageInbox = !isMine && Boolean(activeTeam) && (isAdmin || Boolean(activeTeam.can_manage));
  const showBusinessLine = activeTeam?.code === "UNDERWRITING";
  const showAssignedColumns = !isMine && filter === "assigned";
  const hasWorkAction = activeTeam?.source === "routed" || activeTeam?.source === "intake";
  const showWorkAction = hasWorkAction && (isMine || canManageInbox) && !showAssignedColumns;
  const pendingColumns = 4
    + (isMine ? 0 : (showAssignedColumns ? 2 : 0))
    + (isMine ? 0 : (canManageInbox ? 1 : 0))
    + (showWorkAction ? 1 : 0)
    + (showBusinessLine ? 1 : 0);

  if (uwRow) {
    return (
      <UnderwritingWorkAction
        row={uwRow}
        onBack={() => {
          setUwRow(null);
          loadQueue();
        }}
      />
    );
  }

  if (templateRow) {
    return (
      <WorkActionTemplate
        row={templateRow}
        team={activeTeam}
        onBack={() => {
          setTemplateRow(null);
          loadQueue();
        }}
      />
    );
  }

  if (!activeTeam) {
    const department = workspace?.label || "This department";
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>{isMine ? "Assigned work items" : department}</h1>
            <p style={styles.subtitle}>{department} has no team queue.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {isMine ? "Assigned work items" : activeTeam.label}
          </h1>
          <p style={styles.subtitle}>
            {isMine
              ? "Work assigned to you."
              : "Everyone on the team sees this list."}
          </p>
        </div>
        <div style={styles.countCard}>
          <div style={styles.countValue}>
            {headerCount == null ? "…" : headerCount.toLocaleString()}
          </div>
          <div style={styles.countLabel}>
            {isMine ? "My Items" : tab === "verified" ? "Verified Records" : tab === "completed" ? "Recently Updated" : "Pending Records"}
          </div>
        </div>
      </div>

      {!isMine && <div style={styles.tabs}>
        {activeTeam?.source === "routed" ? (
          <button
            type="button"
            style={tab === "pending" ? styles.tabActive : styles.tab}
            onClick={() => {
              setTab("pending");
              setPage(1);
              setSelectedIds([]);
            }}
          >
            Pending Records
          </button>
        ) : (
          <>
            <button
              type="button"
              style={tab === "pending" ? styles.tabActive : styles.tab}
              onClick={() => {
                setTab("pending");
                setPage(1);
                setSelectedIds([]);
              }}
            >
              Pending Review
            </button>
            {SHOW_VERIFIED_RECORDS && (
              <button
                type="button"
                style={tab === "verified" ? styles.tabActive : styles.tab}
                onClick={() => {
                  setTab("verified");
                  setPage(1);
                  setSelectedIds([]);
                }}
              >
                Verified Records
              </button>
            )}
          </>
        )}
        <button
          type="button"
          style={tab === "completed" ? styles.tabActive : styles.tab}
          onClick={() => {
            setTab("completed");
            setPage(1);
            setSelectedIds([]);
          }}
        >
          Recently Updated
        </button>
      </div>}

      <div style={styles.filters}>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Sender Email</span>
          <input
            type="text"
            value={senderEmail}
            onChange={(event) => setSenderEmail(event.target.value)}
            placeholder="Search sender email"
            style={styles.input}
          />
        </label>
        {tab === "completed" && (
          <>
            <label style={styles.field}>
              <span style={styles.fieldLabel}>Policy number</span>
              <input
                type="text"
                value={policyNumber}
                onChange={(event) => setPolicyNumber(event.target.value)}
                placeholder="Search policy number"
                style={styles.input}
              />
            </label>
            <label style={styles.field}>
              <span style={styles.fieldLabel}>{operationsOutcomes ? "By" : "Completed by"}</span>
              <input
                type="text"
                value={completedBy}
                onChange={(event) => setCompletedBy(event.target.value)}
                placeholder="Search user email"
                style={styles.input}
              />
            </label>
          </>
        )}
        <label style={styles.field}>
          <span style={styles.fieldLabel}>{operationsOutcomes ? "From" : tab === "completed" ? "Completed from" : "From Date & Time"}</span>
          <input
            type="datetime-local"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>{operationsOutcomes ? "To" : tab === "completed" ? "Completed to" : "To Date & Time"}</span>
          <input
            type="datetime-local"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            style={styles.input}
          />
        </label>
        <button type="button" style={styles.primaryButton} onClick={search}>
          Search
        </button>
        <button type="button" style={styles.secondaryButton} onClick={clearFilters}>
          Clear
        </button>
      </div>

      {(error || selectError) && (
        <div style={styles.error}>{selectError || error}</div>
      )}

      {tab === "completed" ? (
        <section style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.thMeta }}>Metadata</th>
                <th style={styles.th}>Email Subject</th>
                <th style={{ ...styles.th, ...styles.thSender }}>Sender</th>
                {operationsOutcomes && <th style={{ ...styles.th, ...styles.thStatus }}>Status</th>}
                <th style={styles.th}>{operationsOutcomes ? "By" : "Completed by"}</th>
                <th style={styles.th}>{operationsOutcomes ? "When" : "Completed"}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td style={styles.empty} colSpan={operationsOutcomes ? 6 : 5}>
                    No routed or completed items.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.msg_id}>
                  <td style={styles.td}>
                    <div style={styles.metaTitle}>
                      {row.document_type || "UNKNOWN"} ·{" "}
                      <span style={styles.policy}>{row.policy_number || "N/A"}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.subject}>
                      <Mail size={14} style={styles.mailIcon} />
                      <span style={styles.subjectText}>{row.email_subject || "—"}</span>
                    </div>
                  </td>
                  <td style={styles.td}>{row.mail_from_address || "—"}</td>
                  {operationsOutcomes && <td style={styles.td}>{outcomeStatus(row)}</td>}
                  <td style={styles.td}>{row.completed_by || "—"}</td>
                  <td style={styles.td}>{formatEntry(row.completed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : tab === "pending" ? (
        <section style={styles.card}>
          {(!isMine || showBusinessLine) && <div style={styles.toolbar}>
            {!isMine && (
            <div style={styles.toolbarLeft}>
              <button
                type="button"
                style={filter === "unassigned" ? styles.chipActive : styles.chip}
                onClick={() => changeFilter("unassigned")}
              >
                Unassigned
              </button>
              <button
                type="button"
                style={filter === "assigned" ? styles.chipActive : styles.chip}
                onClick={() => changeFilter("assigned")}
              >
                Assigned
                {assignedTotal > 0 && (
                  <span style={styles.chipCount}>{assignedTotal}</span>
                )}
              </button>
              <button
                type="button"
                style={filter === "returned" ? styles.chipActive : styles.chip}
                onClick={() => changeFilter("returned")}
              >
                Returned
                {returnedTotal > 0 && (
                  <span style={styles.chipCount}>{returnedTotal}</span>
                )}
              </button>
            </div>
            )}
            {!isMine && <div style={styles.toolbarCenter}>
              {filter === "assigned" && (
                <label style={styles.assigneeFilter}>
                  <span style={styles.lineTriggerLabel}>Assignee</span>
                  <select
                    value={assigneeId}
                    onChange={(event) => applyAssignee(event.target.value)}
                    style={styles.assigneeSelect}
                    aria-label="Filter by assignee"
                  >
                    <option value="">All assignees</option>
                    {assigneeHolders.map((holder) => (
                      <option key={holder.user_id} value={holder.user_id}>
                        {displayName(holder)} ({holder.item_count})
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>}
            <div style={styles.toolbarRight}>
              {showBusinessLine && (
                <BusinessLineFilter
                  options={lineOptions}
                  applied={businessLines}
                  onApply={applyBusinessLines}
                />
              )}
              {showBusinessLine && canManageInbox && selectedIds.length > 0 && (
                <span style={styles.toolbarDivider} />
              )}
              {canManageInbox && selectedIds.length > 0 && (
                <span style={styles.selectedText}>
                  {selectedIds.length} selected
                </span>
              )}
              {canManageInbox && selectedIds.length > 0 && filter === "assigned" && (
                <button
                  type="button"
                  style={{
                    ...styles.secondaryButton,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onClick={() => setRevertOpen(true)}
                >
                  <Undo2 size={14} />
                  Revert
                </button>
              )}
              {canManageInbox && selectedIds.length > 0 && !selectError && (
                <button
                  type="button"
                  style={{
                    ...styles.primaryButton,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onClick={() => setAssignOpen(true)}
                >
                  <UserRound size={14} />
                  {filter === "assigned" ? "Reassign" : "Assign"}
                </button>
              )}
            </div>
          </div>}
          <table style={styles.table}>
            <thead>
              <tr>
                {canManageInbox && (
                  <th style={{ ...styles.th, ...styles.thCheck, position: "relative" }}>
                    <span style={styles.selectHead} ref={selectMenuRef}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                      <button
                        type="button"
                        style={styles.selectCaret}
                        aria-label="Selection options"
                        onClick={() => setSelectMenuOpen((open) => !open)}
                      >
                        <ChevronDown size={14} />
                      </button>
                      {selectMenuOpen && (
                        <div style={styles.selectMenu}>
                          <div style={styles.selectMenuCount}>
                            <input
                              type="number"
                              min={1}
                              max={SELECT_LIMIT}
                              value={selectCount}
                              onChange={(event) => {
                                setSelectCount(event.target.value);
                                setSelectError("");
                              }}
                              placeholder="50"
                              aria-label="Number of items to select"
                              style={styles.selectCount}
                            />
                            <button
                              type="button"
                              style={styles.selectMenuItem}
                              disabled={selecting}
                              onClick={selectBatch}
                            >
                              Select
                            </button>
                          </div>
                          <button
                            type="button"
                            style={{
                              ...styles.selectMenuItem,
                              color: selectedIds.length ? "#0f172a" : "#94a3b8",
                              cursor: selectedIds.length ? "pointer" : "default",
                            }}
                            disabled={!selectedIds.length}
                            onClick={() => {
                              setSelectedIds([]);
                              setSelectMenuOpen(false);
                              setSelectError("");
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </span>
                  </th>
                )}
                <th style={{ ...styles.th, ...styles.thMeta }}>Metadata</th>
                <th style={styles.th}>Email Subject</th>
                <th style={{ ...styles.th, ...styles.thSender }}>Sender</th>
                {showBusinessLine && <th style={{ ...styles.th, ...styles.thLine }}>Business line</th>}
                {!isMine && filter === "assigned" && <th style={{ ...styles.th, ...styles.thAssignee }}>Assignee</th>}
                {showAssignedColumns && <th style={{ ...styles.th, ...styles.thStatus }}>Status</th>}
                <th style={{ ...styles.th, ...styles.thAttach }}>Attachments</th>
                {showWorkAction && (
                  <th style={{ ...styles.th, ...styles.thAction }}>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td style={styles.empty} colSpan={pendingColumns}>
                    No items in this view.
                  </td>
                </tr>
              )}
              {pendingRows.map((row) => (
                <Fragment key={row.msg_id}>
                  <tr
                    style={
                      selectedIds.some((id) => sameId(id, row.msg_id))
                        ? styles.selectedRow
                        : undefined
                    }
                  >
                    {canManageInbox && (
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedIds.some((id) => sameId(id, row.msg_id))}
                          onChange={() => toggleRow(row.msg_id)}
                        />
                      </td>
                    )}
                    <td style={styles.td}>
                      <div style={styles.metaTitle}>
                        {row.document_type || "UNKNOWN"} ·{" "}
                        <span style={styles.policy}>{row.policy_number || "N/A"}</span>
                      </div>
                      <div style={styles.metaSub}>
                        <Clock3 size={10} style={styles.metaIcon} />
                        <span style={styles.metaEntry}>Entry: {formatEntry(row.received_datetime)}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.subject}>
                        <Mail size={14} style={styles.mailIcon} />
                        {showAssignedColumns ? (
                          <AssignedSubject text={row.email_subject} />
                        ) : (
                          <span style={styles.subjectText}>{row.email_subject || "—"}</span>
                        )}
                      </div>
                      {filter === "returned" && (
                        <ReturnReason reason={row.return_reason} />
                      )}
                    </td>
                    <td style={styles.td}>{row.mail_from_address || "—"}</td>
                    {showBusinessLine && (
                      <td style={styles.td}>
                        <BusinessLineBadge lob={row.lob} options={lineOptions} />
                      </td>
                    )}
                    {!isMine && filter === "assigned" && (
                      <td style={styles.td}>
                        <div style={styles.assignee}>
                          <span style={styles.avatar}>
                            {initials(row.assigned_to_email)}
                          </span>
                          <span style={styles.assigneeEmail} title={row.assigned_to_email || ""}>
                            {row.assigned_to_email || "—"}
                          </span>
                        </div>
                      </td>
                    )}
                    {showAssignedColumns && (
                      <td style={styles.td}>
                        {formatQueueStatus(row.queue_status)}
                      </td>
                    )}
                    <td style={styles.td}>
                      <AttachmentCount
                        count={(row.attachments || []).length}
                        loaded={row.attachmentsLoaded !== false}
                        expanded={expandedId === row.msg_id}
                        onToggle={() => toggleAttachments(row)}
                      />
                    </td>
                    {showWorkAction && (
                      <td style={styles.td}>
                        <button
                          type="button"
                          title="Edit and verify"
                          style={styles.actionButton}
                          onClick={(event) => {
                            const rect = event.currentTarget.closest("tr")?.getBoundingClientRect();
                            openReview(row, rect && {
                              top: rect.top,
                              left: rect.left,
                              width: rect.width,
                              height: rect.height,
                            });
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                  {expandedId === row.msg_id && (
                    <AttachmentList
                      files={row.attachments || []}
                      colSpan={pendingColumns}
                      onPreview={handlePreview}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      ) : (
        <section style={styles.card}>
          <div style={styles.toolbar}>
            <span style={styles.sectionTitle}>Verified Records</span>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.thMeta }}>Metadata</th>
                <th style={styles.th}>Email Subject</th>
                <th style={{ ...styles.th, ...styles.thSender }}>Sender</th>
                <th style={styles.th}>Verified By</th>
                <th style={{ ...styles.th, ...styles.thAttach }}>Attachments</th>
                <th style={{ ...styles.th, ...styles.thAction }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && verifiedRows.length === 0 && (
                <tr>
                  <td style={styles.empty} colSpan={6}>
                    No verified records.
                  </td>
                </tr>
              )}
              {verifiedRows.map((row) => (
                <Fragment key={row.msg_id}>
                  <tr>
                    <td style={styles.td}>
                      <div style={styles.metaTitle}>
                        {row.document_type || "UNKNOWN"} ·{" "}
                        <span style={styles.policy}>{row.policy_number || "N/A"}</span>
                      </div>
                      <div style={styles.metaSub}>
                        <Clock3 size={10} style={styles.metaIcon} />
                        <span style={styles.metaEntry}>Entry: {formatEntry(row.created_at)}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.subject}>
                        <Mail size={14} style={styles.mailIcon} />
                        <span style={styles.subjectText}>{row.email_subject || "—"}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{row.mail_from_address || "—"}</td>
                    <td style={{ ...styles.td, color: "#2563eb" }}>
                      {row.verified_by || "—"}
                    </td>
                    <td style={styles.td}>
                      <AttachmentCount
                        count={(row.attachments || []).length}
                        expanded={expandedId === row.msg_id}
                        onToggle={() =>
                          setExpandedId((current) =>
                            current === row.msg_id ? null : row.msg_id,
                          )
                        }
                      />
                    </td>
                    <td style={styles.td}>
                      <button
                        type="button"
                        title="View record"
                        style={styles.actionButton}
                        onClick={() => setReviewRow(row)}
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                  {expandedId === row.msg_id && (
                    <AttachmentList
                      files={row.attachments || []}
                      colSpan={6}
                      onPreview={handlePreview}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(pagedTotal / PAGE_SIZE))}
        totalRecords={pagedTotal}
        pageSize={PAGE_SIZE}
        currentPageCount={visibleRows.length}
        onPageChange={(nextPage) => {
          setPage(nextPage);
          setExpandedId(null);
        }}
      />

      {reviewRow && (
        <MetadataReviewPanel
          row={reviewRow}
          readOnly={tab === "verified"}
          hasNext={
            visibleRows.findIndex((row) => row.msg_id === reviewRow.msg_id) <
            visibleRows.length - 1
          }
          origin={reviewOrigin}
          onClose={() => {
            setReviewRow(null);
            setReviewOrigin(null);
          }}
          onAdvance={() => {
            const index = visibleRows.findIndex((row) => row.msg_id === reviewRow.msg_id);
            const next = index >= 0 ? visibleRows[index + 1] : null;
            setReviewRow(next || null);
            loadQueue();
          }}
        />
      )}

      {revertOpen && (
        <div style={styles.backdrop}>
          <div style={{ ...styles.modal, padding: 16 }}>
            <h2 style={styles.modalTitle}>Return to Unassigned</h2>
            <p style={styles.modalText}>
              {selectedIds.length} assigned item{selectedIds.length === 1 ? "" : "s"} will
              leave the assignee and show under Unassigned again.
            </p>
            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={reverting}
                onClick={() => setRevertOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  opacity: reverting ? 0.45 : 1,
                }}
                disabled={reverting}
                onClick={revertSelected}
              >
                {reverting ? "Reverting..." : "Revert"}
              </button>
            </div>
          </div>
        </div>
      )}
      {assignOpen && activeTeam?.code && (
        <AssignDialog
          msgIds={selectedIds}
          reassign={filter === "assigned"}
          teamCode={activeTeam.code}
          teamLabel={activeTeam.label}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            setAssignOpen(false);
            setSelectedIds([]);
            loadQueue();
          }}
        />
      )}
    </div>
  );
}

function AttachmentCount({ count, loaded = true, expanded, onToggle }) {
  return (
    <button
      type="button"
      style={{
        ...styles.attachment,
        borderColor: expanded ? "#2563eb" : "#e2e8f0",
      }}
      onClick={onToggle}
    >
      <FileText size={12} /> {expanded && loaded ? count : "View"}
    </button>
  );
}

function AttachmentList({ files, colSpan, onPreview }) {
  return (
    <tr>
      <td colSpan={colSpan} style={styles.attachmentRow}>
        <div style={styles.attachmentPanel}>
          {files.map((file) => (
            <div key={`${file.type}-${file.path}`} style={styles.attachmentCard}>
              <span style={styles.subject}>
                <FileText size={14} style={styles.mailIcon} />
                <span style={styles.subjectText}>{file.name || "File"}</span>
              </span>
              <button
                type="button"
                title="Preview document"
                style={styles.previewButton}
                onClick={() => onPreview(file.path)}
              >
                <Eye size={14} />
              </button>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

function AssignDialog({ msgIds, reassign = false, teamCode, teamLabel, onClose, onAssigned }) {
  const { instance, accounts } = useMsal();
  const [users, setUsers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownersReady, setOwnersReady] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [droppedOwners, setDroppedOwners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    callApi(instance, accounts, "GET", "/api/team-queue/assignees", null, {
      params: { team_code: teamCode },
    })
      .then((response) => setUsers(response?.data?.data || []))
      .catch((loadError) =>
        setError(loadError.message || "Failed to load team members"),
      );
  }, [accounts, instance, teamCode]);

  useEffect(() => {
    callApi(instance, accounts, "POST", "/api/team-queue/assignment-owners", {
      msg_ids: msgIds,
      team_code: teamCode,
    })
      .then((response) => setOwners(response?.data?.data || []))
      .catch((loadError) =>
        setError(loadError.message || "Failed to load current assignees"),
      )
      .finally(() => setOwnersReady(true));
  }, [accounts, instance, msgIds, teamCode]);

  const ownerById = useMemo(() => {
    const map = new Map();
    owners.forEach((row) => map.set(String(row.msg_id), row));
    return map;
  }, [owners]);
  const dropped = new Set(droppedOwners.map((id) => String(id)));
  const activeIds = msgIds.filter((id) => {
    const ownerId = ownerById.get(String(id))?.assigned_to_user_id;
    return !(ownerId && dropped.has(String(ownerId)));
  });
  const filtered = users.filter((user) =>
    displayName(user).toLowerCase().includes(query.toLowerCase()),
  );
  const chosen = selectedUsers
    .map((id) => users.find((user) => sameId(user.user_id, id)))
    .filter(Boolean);
  const chosenIds = new Set(chosen.map((user) => String(user.user_id)));
  const staying = [];
  const moving = [];
  activeIds.forEach((id) => {
    const ownerId = ownerById.get(String(id))?.assigned_to_user_id;
    if (ownerId && chosenIds.has(String(ownerId))) staying.push(ownerId);
    else moving.push(id);
  });
  const holderGroups = [];
  const holderIndex = new Map();
  msgIds.forEach((id) => {
    const owner = ownerById.get(String(id));
    const ownerId = owner?.assigned_to_user_id;
    if (!ownerId) return;
    const key = String(ownerId);
    if (!holderIndex.has(key)) {
      holderIndex.set(key, holderGroups.length);
      holderGroups.push({
        user_id: ownerId,
        name: displayName(owner),
        count: 0,
        dropped: dropped.has(key),
      });
    }
    holderGroups[holderIndex.get(key)].count += 1;
  });
  const stayCounts = [];
  const stayIndex = new Map();
  staying.forEach((ownerId) => {
    const key = String(ownerId);
    if (!stayIndex.has(key)) {
      const owner = owners.find((row) => sameId(row.assigned_to_user_id, ownerId));
      stayIndex.set(key, stayCounts.length);
      stayCounts.push({ user_id: ownerId, name: owner ? displayName(owner) : "Assignee", count: 0 });
    }
    stayCounts[stayIndex.get(key)].count += 1;
  });
  const splits = chosen.length && moving.length ? splitCounts(moving.length, chosen) : [];
  const nothingToMove = ownersReady && chosen.length > 0 && moving.length === 0;

  const singleItem = activeIds.length === 1;

  const toggleUser = (userId) => {
    setSelectedUsers((current) => {
      if (singleItem) {
        return current.some((id) => sameId(id, userId)) ? [] : [userId];
      }
      if (current.some((id) => sameId(id, userId))) {
        return current.filter((id) => !sameId(id, userId));
      }
      if (current.length >= activeIds.length) {
        return current;
      }
      return [...current, userId];
    });
  };

  const toggleDropped = (userId) => {
    const key = String(userId);
    setDroppedOwners((current) => (
      current.some((id) => String(id) === key)
        ? current.filter((id) => String(id) !== key)
        : [...current, userId]
    ));
  };

  const confirm = async () => {
    setSaving(true);
    setError("");
    try {
      await callApi(instance, accounts, "POST", "/api/team-queue/assign", {
        msg_ids: activeIds,
        user_ids: chosen.map((user) => user.user_id),
        team_code: teamCode,
      });
      onAssigned();
    } catch (saveError) {
      setError(saveError.message || "Failed to assign items");
      setSaving(false);
    }
  };

  const actionLabel = reassign ? "Reassign" : "Assign";

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.dialogTitle}>{actionLabel} Items</h2>
          <p style={styles.dialogText}>
            <strong>{msgIds.length}</strong> item{msgIds.length === 1 ? "" : "s"} selected · {teamLabel}
          </p>
        </div>
        <div style={styles.modalBody}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search team members"
          style={styles.dialogInput}
        />
        <div style={styles.userList}>
          {filtered.map((user) => (
            <label key={user.user_id} style={styles.userRow}>
              <input
                type={singleItem ? "radio" : "checkbox"}
                name="assignee"
                checked={selectedUsers.some((id) => sameId(id, user.user_id))}
                disabled={
                  !singleItem &&
                  !selectedUsers.some((id) => sameId(id, user.user_id)) &&
                  selectedUsers.length >= activeIds.length
                }
                onChange={() => toggleUser(user.user_id)}
              />
              <span style={styles.dialogAvatar}>{initials(displayName(user))}</span>
              <span style={styles.dialogName}>{displayName(user)}</span>
            </label>
          ))}
          {filtered.length === 0 && (
            <div style={styles.empty}>No team members match your search.</div>
          )}
        </div>
        {holderGroups.length > 0 && (
          <div style={styles.splitBox}>
            <div style={styles.fieldLabel}>Already assigned in this selection</div>
            {holderGroups.map((group) => (
              <div key={group.user_id} style={styles.splitRow}>
                <span>
                  {group.name} — {group.count} item{group.count === 1 ? "" : "s"}
                  {group.dropped ? " left in place" : ""}
                </span>
                <button
                  type="button"
                  style={styles.dropButton}
                  onClick={() => toggleDropped(group.user_id)}
                >
                  {group.dropped ? "Include" : "Drop"}
                </button>
              </div>
            ))}
          </div>
        )}
        {stayCounts.length > 0 && (
          <div style={styles.splitBox}>
            <div style={styles.fieldLabel}>Staying with the checked person</div>
            {stayCounts.map((group) => (
              <div key={group.user_id} style={styles.splitRow}>
                <span>{group.name}</span>
                <strong>{group.count} stay{group.count === 1 ? "s" : ""}</strong>
              </div>
            ))}
          </div>
        )}
        {splits.length > 0 && (
          <div style={styles.splitBox}>
            <div style={styles.fieldLabel}>
              Moving — {moving.length} item{moving.length === 1 ? "" : "s"} across {splits.length} member{splits.length === 1 ? "" : "s"}
            </div>
            {splits.map(({ user, count }) => (
              <div key={user.user_id} style={styles.splitRow}>
                <span>{displayName(user)}</span>
                <strong>
                  {count} item{count === 1 ? "" : "s"}
                </strong>
              </div>
            ))}
          </div>
        )}
        {nothingToMove && (
          <div style={styles.dialogNote}>
            Nothing to reassign. The selected people already have these items.
          </div>
        )}
        {error && <div style={styles.dialogNote}>{error}</div>}
        </div>
        <div style={styles.modalActions}>
          <button type="button" style={{ ...styles.secondaryButton, ...styles.dialogButton }} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              ...styles.dialogButton,
              opacity: chosen.length && moving.length && ownersReady && !saving ? 1 : 0.45,
            }}
            disabled={!chosen.length || !moving.length || !ownersReady || saving}
            onClick={confirm}
          >
            {chosen.length === 1
              ? `${actionLabel} to ${displayName(chosen[0]).split(" ")[0]}`
              : `Confirm ${actionLabel.toLowerCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 24, maxWidth: 1400 },
  header: { display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 20 },
  title: { margin: 0, fontSize: 28, color: "#0f172a" },
  subtitle: { margin: "6px 0 0", color: "#64748b", fontSize: 14 },
  countCard: {
    minWidth: 140,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "12px 20px",
    textAlign: "right",
  },
  countValue: { fontSize: 28, fontWeight: 700, color: "#2563eb" },
  countLabel: { fontSize: 12, color: "#64748b" },
  tabs: { display: "flex", gap: 8, marginBottom: 16 },
  tab: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    borderRadius: 6,
    padding: "8px 18px",
    cursor: "pointer",
  },
  tabActive: {
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#fff",
    borderRadius: 6,
    padding: "8px 18px",
    cursor: "pointer",
  },
  filters: {
    display: "flex",
    gap: 12,
    alignItems: "end",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  field: { flex: 1, display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  input: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 14,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    borderRadius: 6,
    padding: "8px 16px",
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    borderRadius: 6,
    padding: "8px 16px",
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    overflow: "visible",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "12px 20px",
    borderBottom: "1px solid #e2e8f0",
  },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" },
  toolbarCenter: { display: "flex", justifyContent: "center", flex: "1 1 auto", minWidth: 0 },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: "0 0 auto",
    marginLeft: "auto",
  },
  assigneeFilter: { display: "inline-flex", alignItems: "center", gap: 8 },
  assigneeSelect: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 13,
    maxWidth: 260,
  },
  toolbarDivider: {
    width: 1,
    alignSelf: "stretch",
    background: "#e2e8f0",
  },
  lineBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.03em",
  },
  lineBadgeFallback: { background: "#f1f5f9", color: "#334155" },
  lineFilter: { position: "relative" },
  lineTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#334155",
    borderRadius: 8,
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 13,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  lineTriggerLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  lineTriggerDivider: { width: 1, alignSelf: "stretch", background: "#e2e8f0" },
  lineTriggerValue: {
    color: "#0f172a",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  linePanel: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    zIndex: 30,
    width: 320,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    padding: 16,
  },
  linePanelHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  linePanelTitle: { fontSize: 14, fontWeight: 700, color: "#0f172a" },
  linePanelMeta: { fontSize: 12, color: "#64748b", marginTop: 2 },
  linePanelLinks: { display: "flex", gap: 12 },
  lineLink: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  lineOptions: { display: "flex", flexDirection: "column", gap: 10, marginTop: 14 },
  lineOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    color: "#0f172a",
    cursor: "pointer",
  },
  linePanelFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 16,
    paddingTop: 12,
    borderTop: "1px solid #e2e8f0",
  },
  linePanelActions: { display: "flex", gap: 8 },
  sectionTitle: { fontWeight: 700, color: "#0f172a" },
  chips: { display: "flex", gap: 8, marginTop: 12 },
  chip: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    borderRadius: 999,
    padding: "4px 12px",
    cursor: "pointer",
  },
  chipActive: {
    border: "1px solid #0f172a",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 999,
    padding: "4px 12px",
    cursor: "pointer",
  },
  chipCount: {
    marginLeft: 6,
    background: "#f1f5f9",
    color: "#64748b",
    borderRadius: 4,
    padding: "1px 5px",
    fontSize: 10,
  },
  selectedText: { fontSize: 12, color: "#64748b" },
  selectHead: { display: "inline-flex", alignItems: "center", gap: 2 },
  selectCaret: {
    border: "none",
    background: "transparent",
    color: "#64748b",
    padding: 0,
    display: "inline-flex",
    cursor: "pointer",
  },
  selectMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 20,
    minWidth: 160,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
    padding: 4,
  },
  selectMenuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    color: "#0f172a",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    cursor: "pointer",
  },
  selectMenuCount: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 8px 8px",
    borderBottom: "1px solid #f1f5f9",
  },
  selectCount: {
    width: 64,
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 13,
    color: "#0f172a",
    background: "#fff",
  },
  table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 10,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#2563eb",
    background: "#fafafa",
    borderBottom: "1px solid #e2e8f0",
  },
  thCheck: { width: 52 },
  thMeta: { width: 240 },
  thSender: { width: 190 },
  thAssignee: { width: 176 },
  thStatus: { width: 112 },
  thAttach: { width: 108 },
  thAction: { width: 72 },
  thLine: { width: 120 },
  td: {
    padding: "10px 16px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 13,
    lineHeight: "18px",
    color: "#475569",
    verticalAlign: "middle",
    overflowWrap: "break-word",
  },
  selectedRow: { background: "#eff6ff" },
  metaTitle: {
    color: "#0f172a",
    fontWeight: 600,
    fontSize: 13,
    lineHeight: "18px",
    whiteSpace: "normal",
    overflowWrap: "normal",
  },
  policy: { color: "#2563eb", fontWeight: 400 },
  metaSub: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    fontSize: 12,
    lineHeight: "16px",
    color: "#64748b",
    whiteSpace: "nowrap",
  },
  metaEntry: { whiteSpace: "nowrap" },
  metaIcon: { width: 10, height: 10, minWidth: 10, flex: "0 0 10px", display: "block" },
  subject: { display: "flex", alignItems: "flex-start", gap: 6, color: "#0f172a", fontSize: 13, lineHeight: "18px" },
  subjectText: { minWidth: 0, flex: "1 1 auto" },
  subjectTextClamp: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  subjectTooltip: {
    position: "fixed",
    width: 320,
    zIndex: 80,
    background: "#0f172a",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 12px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.22)",
  },
  subjectTooltipText: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  subjectCopy: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    border: "none",
    borderRadius: 6,
    background: "rgba(255, 255, 255, 0.12)",
    color: "#fff",
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "16px",
    cursor: "pointer",
  },
  returnReason: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: 280,
    marginTop: 6,
    marginLeft: 20,
    background: "#fff4e8",
    color: "#ea580c",
    borderRadius: 6,
    padding: "3px 6px 3px 8px",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "16px",
  },
  returnReasonIcon: { width: 12, height: 12, minWidth: 12, flex: "0 0 12px" },
  returnReasonText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  returnInfo: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    border: "none",
    background: "transparent",
    color: "#ea580c",
    padding: 0,
    cursor: "help",
  },
  returnTooltip: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    right: -6,
    width: 248,
    background: "#0f172a",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    textAlign: "left",
    whiteSpace: "normal",
    zIndex: 40,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.22)",
  },
  mailIcon: { width: 14, height: 14, minWidth: 14, minHeight: 14, flex: "0 0 14px", marginTop: 2 },
  assignee: { display: "flex", alignItems: "center", gap: 8, minWidth: 0, color: "#0f172a" },
  assigneeEmail: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#eff6ff",
    color: "#2563eb",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 700,
  },
  pendingBadge: {
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #cbd5e1",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
  },
  progress: {
    background: "#eff6ff",
    color: "#2563eb",
    border: "1px solid #bfdbfe",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
  },
  attachment: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: "1px solid #e2e8f0",
    borderRadius: 4,
    padding: "2px 8px",
    background: "#f8fafc",
    cursor: "pointer",
    color: "#475569",
  },
  noAttachment: { color: "#94a3b8", fontSize: 12 },
  actionButton: {
    border: "none",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 8,
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  previewButton: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    borderRadius: 6,
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  attachmentRow: { background: "#f8fafc", padding: "8px 16px" },
  attachmentPanel: { display: "flex", flexDirection: "column", gap: 8 },
  attachmentCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
  },
  empty: { textAlign: "center", color: "#94a3b8", padding: 32 },
  error: { color: "#b91c1c", fontSize: 13, margin: "8px 0" },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.35)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: 24,
    overflow: "auto",
    zIndex: 80,
  },
  modal: {
    width: 420,
    maxWidth: "100%",
    maxHeight: "calc(100vh - 48px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: "#fff",
    borderRadius: 12,
    margin: "auto",
  },
  modalHeader: { padding: "14px 16px 0", flexShrink: 0 },
  modalBody: { overflow: "auto", padding: "10px 16px 12px", minHeight: 0 },
  dialogTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: "#0f172a" },
  dialogText: { margin: "4px 0 0", color: "#64748b", fontSize: 12 },
  dialogInput: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 12,
    boxSizing: "border-box",
  },
  dialogName: { fontSize: 12, color: "#0f172a" },
  dialogAvatar: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#eff6ff",
    color: "#2563eb",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 8,
    fontWeight: 700,
    flex: "0 0 18px",
  },
  dialogNote: { color: "#b91c1c", fontSize: 12, margin: "8px 0 0" },
  dialogButton: { fontSize: 12, padding: "6px 12px" },
  modalTitle: { margin: 0, fontSize: 16, color: "#0f172a" },
  modalText: { color: "#64748b", fontSize: 14 },
  userList: { maxHeight: 160, overflow: "auto", marginTop: 8 },
  userRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 2px" },
  splitBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 8,
    marginTop: 8,
  },
  splitRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 12, marginTop: 4 },
  dropButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "10px 16px 14px",
    borderTop: "1px solid #f1f5f9",
    flexShrink: 0,
  },
};
